import type { FilterOption } from './types';

/*
 * Option loading for `async` filters, with no data library: debounced,
 * aborted when superseded, cached per filter and query for a minute, and
 * every option seen is remembered so a chip can label a value the moment it
 * is picked (or when it comes back from the URL, via `resolveOptions`).
 */

export const CACHE_MS = 60_000;
export const optionCache = new Map<string, { at: number; options: readonly FilterOption[] }>();
export const seenOptions = new Map<string, Map<string, FilterOption>>();
// `resolveOptions` calls in flight, so chips showing the same values (the
// search bar measures a hidden copy of each) share one request.
export const resolving = new Map<string, Promise<readonly FilterOption[]>>();

export const NO_OPTIONS: readonly FilterOption[] = [];

export function remember(filterKey: string, options: readonly FilterOption[]) {
  let known = seenOptions.get(filterKey);
  if (!known) {
    known = new Map();
    seenOptions.set(filterKey, known);
  }
  for (const option of options) known.set(option.value, option);
}

/**
 * Enum options from the loaded rows, for client-mode tables whose values are
 * not known up front: `options: uniqueOptions(products, (p) => p.vendor)`.
 */
export function uniqueOptions<TData>(
  rows: readonly TData[],
  getValue: (row: TData) => unknown,
  getLabel: (value: string) => string = (value) => value,
): FilterOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const raw = getValue(row);
    const values = Array.isArray(raw) ? raw : [raw];
    for (const value of values) {
      if (value == null || value === '') continue;
      const key = String(value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, count]) => ({ value, label: getLabel(value), count }));
}
